import Tab from "@/components/ui/tab";
import { Heading } from "@elaniintc/goes-design-system";
import UsersTable from "../users-table";
import SearchInput from "@/components/common/search-input";
import PermissionsList from "../../permissions-list";
import TextField, { TextAreaField } from "@/components/common/text-field";
import PermissionType from "../../permission-type";
import EditConfirmationModal from "./confirmation-modal";
import AssignUserDropdown from "../assign-user-dropdown";
import type { UserType } from "@/types/entities/users";
import { useRoleEdit } from "./use-role-edit";

const RoleEdit = () => {
  const {
    role,
    modalState,
    setModalState,
    tempData,
    onSubmit,
    handleSubmit,
    onPreSubmit,
    register,
    errors,
    name,
    description,
    roleTypeByPermissions,
    canAssignPermissions,
    canAssingRoleToUsers,
    activeTab,
    setActiveTab,
    setLocalSearchQuery,
    handleUserAssigment,
    selectedUsersToAssign,
    isLoadingPermissions,
    isLoadingRolePermissions,
    filterPermissions,
    handleCheckboxChange,
    localRolePermissions,
    users,
    isLoadingUsers,
    selectedUsersToUnassign,
    onUnassign,
  } = useRoleEdit();

  return (
    <div className="h-full grid grid-cols-1 desktop:grid-cols-2 gap-8">
      <EditConfirmationModal
        isOpen={modalState === "edit"}
        onConfirm={() => {
          setModalState("idle");
          if (tempData) {
            onSubmit(tempData);
          }
        }}
        onCancel={() => {
          setModalState("idle");
        }}
      />
      <form
        id="edit-role-form"
        data-testid="edit-role-form"
        onSubmit={handleSubmit(onPreSubmit)}
        className="h-full w-full flex flex-col"
      >
        <Heading variant="h5">Datos del rol</Heading>
        <div className="flex flex-col gap-6 pt-4">
          <TextField
            id="name"
            label="Nombre"
            placeholder="Ingrese el nombre del rol"
            className="w-full"
            variant="lg"
            required
            {...register("name")}
            maxLength={125}
            limit={125}
            count={name.length}
            error={errors.name?.message || ""}
            disabled={role?.data?.is_editable === false}
          />
          <TextAreaField
            id="description"
            label="Descripción"
            placeholder="Ingrese una descripción del rol"
            className="w-full"
            rows={4}
            required
            {...register("description")}
            maxLength={255}
            limit={255}
            count={description.length}
            error={errors.description?.message || ""}
            disabled={role?.data?.is_editable === false}
          />
        </div>
        <div className="flex flex-col gap-2 pt-8">
          <Heading variant="h5">Tipo de rol</Heading>
          <p className="leading-3xl text-body font-normal">
            El tipo de rol está determinado por los permisos activos para los
            usuarios. Aquí podrás ver qué tipo de rol tendrán los usuarios que
            añadas según los permisos que vayas seleccionando.
          </p>
          <div className="pt-6">
            <PermissionType
              name="Administrador"
              description="Permisos de edición sobre Roles y Usuarios"
              isActive={roleTypeByPermissions === "admin"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Editor"
              description="Permisos de edición sobre Procesos, Flujos y demás."
              isActive={roleTypeByPermissions === "editor"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
            />
            <PermissionType
              name="Lector"
              description="Permisos de lectura en todas las partes del sistema."
              isActive={roleTypeByPermissions === "reader"}
              isTypeEmpty={roleTypeByPermissions === "empty"}
              showDivider={false}
            />
          </div>
        </div>
      </form>

      {(canAssignPermissions || canAssingRoleToUsers) && (
        <div className="flex flex-col w-full h-fit shadow-custom bg-neutral-object border-neutral border-[0.5px] rounded-xl overflow-hidden [@media(min-height:800px)]:max-h-[calc(100vh-200px)] max-h-169.5">
          <div className="flex justify-between items-center px-3">
            <div className="relative pt-3">
              {canAssignPermissions && (
                <Tab
                  active={activeTab === "permissions"}
                  onClick={() => setActiveTab("permissions")}
                >
                  Permisos
                </Tab>
              )}
              <Tab
                active={activeTab === "users"}
                onClick={() => setActiveTab("users")}
              >
                Usuarios
              </Tab>
            </div>
            {activeTab === "permissions" && canAssignPermissions && (
              <SearchInput
                variant="xs"
                placeholder="Buscar permisos..."
                onChange={({ target }) => {
                  setLocalSearchQuery(target.value);
                }}
              />
            )}
            {activeTab === "users" && canAssingRoleToUsers && (
              <div className="pt-3 pb-2">
                <AssignUserDropdown
                  onAddUser={(user) => {
                    handleUserAssigment(user as UserType);
                  }}
                  selectedUsers={selectedUsersToAssign}
                />
              </div>
            )}
          </div>
          {activeTab === "permissions" && canAssignPermissions && (
            <PermissionsList
              isLoading={isLoadingPermissions || isLoadingRolePermissions}
              items={filterPermissions}
              handleCheckboxChange={handleCheckboxChange}
              selectedItems={localRolePermissions.map((perm) => perm.id)}
              canEdit={role?.data?.is_editable !== false}
            />
          )}
          {activeTab === "users" && (
            <UsersTable
              data={[
                ...(selectedUsersToAssign || []),
                ...(users?.data || []).filter((user) => {
                  return !selectedUsersToUnassign.includes(
                    user.keycloak_id || "",
                  );
                }),
              ]}
              isLoading={!users?.data && isLoadingUsers}
              onUnassign={onUnassign}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default RoleEdit;
